import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, type MembersResponse } from "../api";
import { useClubs } from "../ClubContext";

type Role = "admin" | "coach" | "viewer";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function roleBadge(role: Role) {
  const cls = {
    admin: "bg-brand-100 text-brand-700",
    coach: "bg-emerald-100 text-emerald-700",
    viewer: "bg-slate-100 text-slate-700",
  }[role];
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>
      {role}
    </span>
  );
}

export function MembersPage() {
  const navigate = useNavigate();
  const { me, activeClub, isActiveClubAdmin: isAdmin, loading: clubsLoading } = useClubs();
  const [data, setData] = useState<MembersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>("coach");
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const clubId = activeClub?.clubId ?? null;

  const loadMembers = useCallback(async (id: string) => {
    try {
      const res = await api<MembersResponse>(`/v1/clubs/${id}/members`);
      setData(res);
      setError(null);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Failed to load";
      setError(msg);
      if (msg.toLowerCase().includes("unauthor")) navigate("/login", { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (!clubId || !isAdmin) return;
    setData(null);
    setInviteMessage(null);
    loadMembers(clubId);
  }, [clubId, isAdmin, loadMembers]);

  async function handleInvite(e: React.FormEvent) {
    e.preventDefault();
    if (!clubId) return;
    setInviting(true);
    setInviteMessage(null);
    try {
      await api(`/v1/clubs/${clubId}/invitations`, {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole }),
      });
      setInviteMessage({ ok: true, text: `Invited ${inviteEmail.trim()}` });
      setInviteEmail("");
      setInviteRole("coach");
      await loadMembers(clubId);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Invite failed";
      setInviteMessage({ ok: false, text: msg });
    } finally {
      setInviting(false);
    }
  }

  async function handleRemove(userId: string, email: string | null) {
    if (!clubId) return;
    if (!confirm(`Remove ${email ?? "this member"} from ${activeClub?.clubName ?? "the club"}?`)) return;
    try {
      await api(`/v1/clubs/${clubId}/members/${userId}`, {
        method: "DELETE",
      });
      await loadMembers(clubId);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Remove failed");
    }
  }

  async function handleResend(invitationId: string, email: string) {
    if (!clubId) return;
    try {
      await api(`/v1/clubs/${clubId}/invitations/${invitationId}/resend`, {
        method: "POST",
      });
      setInviteMessage({ ok: true, text: `Resent invitation to ${email}` });
    } catch (e) {
      alert(e instanceof Error ? e.message : "Resend failed");
    }
  }

  async function handleCancel(invitationId: string, email: string) {
    if (!clubId) return;
    if (!confirm(`Cancel the invitation to ${email}?`)) return;
    try {
      await api(`/v1/clubs/${clubId}/invitations/${invitationId}`, {
        method: "DELETE",
      });
      await loadMembers(clubId);
    } catch (e) {
      alert(e instanceof Error ? e.message : "Cancel failed");
    }
  }

  return (
    <main className="mx-auto max-w-6xl space-y-8 px-6 py-8">
        <section>
          <h2 className="mb-4 text-lg font-semibold">
            Members{activeClub && ` — ${activeClub.clubName}`}
          </h2>
          {error && <p className="mb-4 text-sm text-rose-600">{error}</p>}
          {!isAdmin && !clubsLoading && !error && (
            <p className="text-sm text-slate-500">
              You need to be a club admin of {activeClub?.clubName ?? "this club"} to view this page.
            </p>
          )}
          {isAdmin && data === null && !error && (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
          {isAdmin && data && (
            <div className="rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Joined</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map((m) => (
                    <tr key={m.userId} className="border-t border-slate-100">
                      <td className="px-4 py-3">{m.name ?? "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{m.email ?? "—"}</td>
                      <td className="px-4 py-3">{roleBadge(m.role)}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(m.joinedAt)}</td>
                      <td className="px-4 py-3 text-right">
                        {m.userId !== me?.user.id && (
                          <button
                            onClick={() => handleRemove(m.userId, m.email)}
                            className="text-xs font-medium text-rose-600 hover:underline"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {isAdmin && data && (
          <section>
            <h2 className="mb-4 text-lg font-semibold">Invite a coach or viewer</h2>
            <form onSubmit={handleInvite} className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                <div className="flex-1">
                  <label className="block text-xs font-semibold text-slate-600">Email</label>
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="mt-1 w-full rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                    placeholder="coach@example.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600">Role</label>
                  <select
                    value={inviteRole}
                    onChange={(e) => setInviteRole(e.target.value as Role)}
                    className="mt-1 rounded-md border border-slate-300 px-3 py-1.5 text-sm"
                  >
                    <option value="admin">admin</option>
                    <option value="coach">coach</option>
                    <option value="viewer">viewer</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={inviting}
                  className="rounded-md bg-brand-600 px-4 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
                >
                  {inviting ? "Sending…" : "Send invite"}
                </button>
              </div>
              {inviteMessage && (
                <p
                  className={`mt-3 text-sm ${
                    inviteMessage.ok ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {inviteMessage.text}
                </p>
              )}
            </form>
          </section>
        )}

        {isAdmin && data && data.invitations.length > 0 && (
          <section>
            <h2 className="mb-4 text-lg font-semibold">Pending invitations</h2>
            <div className="rounded-xl border border-slate-200 bg-white">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Role</th>
                    <th className="px-4 py-2">Invited</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.invitations.map((inv) => (
                    <tr key={inv.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-slate-600">{inv.email}</td>
                      <td className="px-4 py-3">{roleBadge(inv.role)}</td>
                      <td className="px-4 py-3 text-slate-500">{formatDate(inv.createdAt)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex gap-3">
                          <button
                            onClick={() => handleResend(inv.id, inv.email)}
                            className="text-xs font-medium text-slate-700 hover:underline"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => handleCancel(inv.id, inv.email)}
                            className="text-xs font-medium text-rose-600 hover:underline"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
    </main>
  );
}
